import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../../utils/analytics/analytics';
import { getModuleFromPath } from '../../utils/analytics/analyticsHelpers';
import { useAnalyticsContext } from '../../context/AnalyticsContext/AnalyticsContext';

/**
 * Hook para trackear automáticamente los cambios de ruta en React Router
 * 
 * Este hook:
 * - Detecta cambios de ruta usando useLocation de React Router
 * - Extrae module de la ruta usando helpers
 * - Establece el módulo en el contexto de analytics
 * - Las secciones se determinan dinámicamente por los componentes usando AnalyticsContext
 * - Envía evento 'polko_page_view' a través de trackPageView
 * 
 * @param {Object} options - Opciones de configuración
 * @param {Function} options.getModuleFromPath - Función personalizada para extraer module (opcional, usa helper por defecto)
 * @param {Function} options.getSectionFromPath - Función personalizada para extraer section (opcional, por defecto retorna null)
 */
export const usePageViewTracker = (options = {}) => {
    const location = useLocation();
    const { getModuleFromPath: customGetModule, getSectionFromPath: customGetSection } = options;
    const { setCurrentModule, setCurrentSection } = useAnalyticsContext();

    useEffect(() => {
        // Extraer module de la ruta usando helpers o función personalizada
        const module = customGetModule 
            ? customGetModule(location.pathname) 
            : getModuleFromPath(location.pathname);
        
        // Las secciones se determinan dinámicamente por los componentes usando AnalyticsContext
        // No establecemos sección desde la ruta para evitar conflictos
        const section = customGetSection
            ? customGetSection(location.pathname, module)
            : null;
        
        // Si no se pudo determinar module, usar 'unknown'
        const resolvedModule = module || 'unknown';
        
        // Establecer en el contexto para que componentes puedan leerlo
        // No establecemos section aquí - los componentes lo harán dinámicamente cuando sea necesario
        setCurrentModule(resolvedModule);
        if (section) {
            setCurrentSection(section);
        } else {
            // No establecer sección por defecto - los componentes la establecerán cuando sea necesario
            setCurrentSection(null);
        }
        
        // Trackear pageview con un pequeño delay para asegurar que el título se haya actualizado
        const timer = setTimeout(() => {
            const pageViewParams = {
                module: resolvedModule,
                pageTitle: document.title
            };
            
            // Incluir section solo si existe (los componentes la establecen dinámicamente)
            // si no hay section, no la incluimos en el evento
            if (section) {
                pageViewParams.section = section;
            }
            
            trackPageView(pageViewParams);
        }, 100);

        return () => clearTimeout(timer);
    }, [location.pathname, customGetModule, customGetSection, setCurrentModule, setCurrentSection]);
};

